import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { KeyRound, ArrowLeft, CheckCircle } from 'lucide-react';

async function hashPassword(password) {
  const msgBuffer = new TextEncoder().encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

const RecuperarSenha = () => {
  const [step, setStep] = useState(1); // 1: e-mail, 2: nova senha
  const [email, setEmail] = useState('');
  const [adminId, setAdminId] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Passo 1: verifica se o e-mail existe na base de administradores
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'admins'));
      const match = snapshot.docs.find(
        (d) => d.data().email === email.toLowerCase().trim()
      );
      if (match) {
        setAdminId(match.id);
        setStep(2);
      } else {
        setError(
          'Este e-mail não está cadastrado como administrador. Peça para um administrador já existente cadastrar você antes de tentar recuperar a senha.'
        );
      }
    } catch (err) {
      setError('Erro ao verificar e-mail. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Passo 2: define a nova senha diretamente
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (novaSenha.length < 6) {
      setError('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      const newPasswordHash = await hashPassword(novaSenha);
      await updateDoc(doc(db, 'admins', adminId), { password: newPasswordHash });
      setSuccess(true);
    } catch (err) {
      setError('Erro ao redefinir senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="vh-100 d-flex align-items-center" style={{ backgroundColor: '#121212' }}>
        <Container>
          <div className="d-flex justify-content-center">
            <Card
              className="p-4 shadow-lg bg-dark text-white border-0 text-center"
              style={{ maxWidth: '420px', width: '100%' }}
            >
              <CheckCircle size={48} color="#FF8C00" className="mx-auto mb-3" />
              <h4 style={{ color: '#FF8C00' }}>Senha redefinida com sucesso!</h4>
              <p className="text-muted mt-2 mb-4">
                Você já pode fazer login com a nova senha.
              </p>
              <Button
                variant="warning"
                className="w-100 fw-bold"
                style={{ backgroundColor: '#FF8C00', border: 'none', color: 'black' }}
                onClick={() => navigate('/area-interna-gestao')}
              >
                Ir para o Login
              </Button>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="vh-100 d-flex align-items-center" style={{ backgroundColor: '#121212' }}>
      <Container>
        <div className="d-flex justify-content-center">
          <Card
            className="p-4 shadow-lg bg-dark text-white border-0"
            style={{ maxWidth: '420px', width: '100%' }}
          >
            <div className="text-center mb-4">
              <KeyRound size={36} color="#FF8C00" />
              <h4 className="mt-2 fw-bold" style={{ color: '#FF8C00' }}>
                Recuperar Senha
              </h4>
              {/* Indicador de etapa */}
              <div className="d-flex justify-content-center gap-2 mt-2">
                <span
                  className="badge rounded-pill"
                  style={{ backgroundColor: '#FF8C00', color: 'black', opacity: step >= 1 ? 1 : 0.4 }}
                >
                  1. E-mail
                </span>
                <span
                  className="badge rounded-pill"
                  style={{ backgroundColor: step >= 2 ? '#FF8C00' : '#555', color: step >= 2 ? 'black' : 'white' }}
                >
                  2. Nova senha
                </span>
              </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {step === 1 ? (
              <Form onSubmit={handleCheckEmail}>
                <p className="text-muted small mb-3">
                  Informe o e-mail cadastrado. Se ele existir no sistema, você poderá cadastrar uma nova senha na próxima tela.
                </p>
                <Form.Group className="mb-4">
                  <Form.Label>E-mail do administrador</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="admin@email.com"
                    className="bg-secondary text-white border-0"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Form.Group>
                <Button
                  variant="warning"
                  type="submit"
                  className="w-100 fw-bold"
                  style={{ backgroundColor: '#FF8C00', border: 'none', color: 'black' }}
                  disabled={loading}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : 'VERIFICAR E-MAIL'}
                </Button>
              </Form>
            ) : (
              <Form onSubmit={handleResetPassword}>
                <p className="text-muted small mb-3">
                  E-mail: <strong className="text-white">{email}</strong>
                </p>
                <Form.Group className="mb-3">
                  <Form.Label>Nova Senha</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    className="bg-secondary text-white border-0"
                    value={novaSenha}
                    onChange={(e) => setNovaSenha(e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-4">
                  <Form.Label>Confirmar Nova Senha</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Repita a nova senha"
                    className="bg-secondary text-white border-0"
                    value={confirmarSenha}
                    onChange={(e) => setConfirmarSenha(e.target.value)}
                    required
                  />
                </Form.Group>
                <Button
                  variant="warning"
                  type="submit"
                  className="w-100 fw-bold"
                  style={{ backgroundColor: '#FF8C00', border: 'none', color: 'black' }}
                  disabled={loading}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : 'REDEFINIR SENHA'}
                </Button>
              </Form>
            )}

            <Button
              variant="link"
              className="mt-3 w-100 text-muted text-decoration-none small"
              onClick={() => navigate('/area-interna-gestao')}
            >
              <ArrowLeft size={14} className="me-1" /> Voltar ao login
            </Button>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default RecuperarSenha;